/** The record editor's `richtext` widget: `RichTextField` (the tour itinerary's, reused)
 *  on a FieldSlot.
 *
 *  - At rest the stored HTML in its read-only box — no Quill mounted — until it is clicked
 *    (or focused and Enter / Space); view mode shows the same box (`View`), so pressing E
 *    moves no text.
 *  - Uncontrolled Quill: only `source === 'user'` edits are reported (QuillEditor), so
 *    opening it never reads as a change.
 *  - Edits commit after a 300 ms pause or when focus leaves; one focus session is ONE
 *    undo step — the session opens the slot's coalesce (`onFocus`) and its end closes it
 *    (`onBlur`).
 *  - The closed box is the button "Edit <label>", the open editor the textbox <label>, both
 *    described by the slot's caption.
 *  - A value reads as its text (`formatChange`: the "was …" caption, the ledger). */
import { Box } from '@mui/material';

import { htmlText } from '@/components/RichText/htmlText';

import { EMPTY_DISPLAY } from '../../editor/format';
import type { RecordWidget, RecordWidgetProps } from '../../editor/types';

import { RICHTEXT_BOX_SX, RichTextDisplay, RichTextField } from './RichTextField';

function RichTextInput(p: RecordWidgetProps) {
  const { id, onFocus, onBlur, onChange } = p;
  return (
    <RichTextField
      id={id}
      label={p.field.label}
      value={typeof p.value === 'string' ? p.value : ''}
      describedBy={p.describedBy}
      error={p.error}
      disabled={p.disabled}
      newSession={() => {
        onFocus();
        return id;
      }}
      onCommit={(html) => onChange(html)}
      onSessionEnd={onBlur}
    />
  );
}

/** The stored HTML read-only, in the closed editor's box exactly. */
export function RichTextView({ value }: { value: unknown }) {
  return (
    <Box data-richtext="view" sx={{ ...RICHTEXT_BOX_SX, flex: '1 1 auto', minWidth: 0 }}>
      <RichTextDisplay html={value} />
    </Box>
  );
}

export const RichTextWidget: RecordWidget = Object.assign(RichTextInput, {
  formatChange: (v: unknown) => htmlText(v) || EMPTY_DISPLAY,
  View: RichTextView,
});
